import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { personIcon, playIcon } from '../../assets/icons';
import { MarkdownContent } from './MarkdownContent';
import MessageMenu from './MessageMenu';
import MessageMenuMobile from './MessageMenuMobile';
import EditMessagePopup from './EditMessagePopup';
import EditMessagePopupMobile from './EditMessagePopupMobile';
import LoadingDots from '../utils/LoadingDots';
import SpotifyTrackEmbed from './SpotifyTrackEmbed';
import styles from './Message.module.css';
import { MessageType, MessageProps } from '../../types/message';
import { MAX_REGENERATIONS } from '../../hooks/active-chat/useRegenerateTracking';

// Three dots component for message options
function MessageOptions({ onClick, isMobile }: { onClick: () => void, isMobile: boolean }): JSX.Element {
  return (
    <button 
      className={styles.messageOptions}
      aria-label="Edit message"
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      ✎
    </button>
  );
}

// Regeneration navigation component
function RegenerationNavigation({ 
  total, 
  current,
  onPrevious,
  onNext,
  onRegenerate,
  isLoading
}: { 
  total: number, 
  current: number,
  onPrevious: () => void,
  onNext: () => void,
  onRegenerate?: () => void,
  isLoading?: boolean
}): JSX.Element | null {
  if (total <= 0) return null;
  
  // Calculate display index (1-based instead of 0-based)
  // total is the number of historical versions
  // current is index of current viewed version (0 = latest/current message)
  // For display purposes, we count in reverse where total+1 is the current message
  const displayCurrent = total + 1 - current;
  const displayTotal = total + 1; // +1 because the current version counts as one more
  
  return (
    <div className={styles.regenerationNavigation}>
      <div className={styles.navigationControls}>
        <button 
          className={styles.navButton}
          onClick={(e) => {
            e.stopPropagation();
            onPrevious();
          }}
          disabled={current === total}
        >
          ◀
        </button>
        <div className={styles.navStatus}>
          {displayCurrent}/{displayTotal}
        </div>
        <button 
          className={styles.navButton}
          onClick={(e) => {
            e.stopPropagation();
            // If we're at the latest regeneration (current === 0), call regenerate
            // But only if we haven't hit the cap
            if (current === 0 && onRegenerate) {
              if (total < MAX_REGENERATIONS) {
                onRegenerate();
              }
            } else {
              onNext();
            }
          }}
          // Disable if:
          // 1. At latest regeneration (current === 0) AND either:
          //    a. No regenerate function available, or
          //    b. Hit max regenerations limit
          // 2. Currently loading
          disabled={
            (current === 0 && (!onRegenerate || total >= MAX_REGENERATIONS)) || 
            isLoading
          }
        >
          ▶
        </button>
      </div>
    </div>
  );
}

const isUserlike = (message: MessageType): boolean => {
  return message.member_type === 'user' || message.member_type === 'persona';
};

const Message: React.FC<MessageProps> = ({ 
  message, 
  isOverlay = false, 
  isLoading = false,
  isLastMessage = false,
  isLastCharacterMessage = false,
  onRegenerate,
  onRewind,
  onNewChat,
  onEdit,
  onAvatarClick,
  onContentLoad,
  regenerationHistory,
  currentRegenerationIndex = 0,
  onSelectRegeneration,
  ...otherProps
}) => {
  const navigate = useNavigate();
  const [menuPosition, setMenuPosition] = useState<{ x: number, y: number } | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const messageRef = useRef<HTMLDivElement>(null);
  
  // Log regeneration history when it changes
  useEffect(() => {
    if (regenerationHistory && regenerationHistory.length > 0) {
      // Removed console.log for regeneration history update
    }
  }, [regenerationHistory, currentRegenerationIndex, message.id]);
  
  // Track regeneration history length to auto-reset to latest version when a new regeneration is added
  const regenerationHistoryLengthRef = useRef<number>(regenerationHistory?.length || 0);
  
  useEffect(() => {
    const currentLength = regenerationHistory?.length || 0;
    const previousLength = regenerationHistoryLengthRef.current;
    
    // If regeneration history length increased, reset to the latest version
    if (currentLength > previousLength && onSelectRegeneration) {
      // Removed console.log for new regeneration detection
      onSelectRegeneration(0);
    }
    
    // Update the ref with current length
    regenerationHistoryLengthRef.current = currentLength;
  }, [regenerationHistory, onSelectRegeneration]);
  
  // Determine if we're viewing a historical regeneration
  const viewingHistoricalVersion = 
    regenerationHistory && 
    regenerationHistory.length > 0 && 
    currentRegenerationIndex > 0;
  
  // Get the content to display (either current or historical)
  let displayContent = message.content;
  
  if (viewingHistoricalVersion && regenerationHistory && regenerationHistory.length > 0) {
    // Calculate the index from the end to get the oldest first
    const historyIndex = regenerationHistory.length - currentRegenerationIndex;
    if (historyIndex >= 0 && historyIndex < regenerationHistory.length) {
      displayContent = regenerationHistory[historyIndex].content;
      // Removed console.log for displaying historical content
    } else {
      console.warn('[Message] History index out of bounds:', {
        messageId: message.id,
        currentRegenerationIndex,
        historyIndex,
        totalHistory: regenerationHistory ? regenerationHistory.length : 0
      });
    }
  } else {
    // Removed console.log for displaying current content
  }
  
  // Determine if this is a regenerating message (vs a new message being generated)
  const isRegenerating = !!(isLoading && message.content);

  // Check if we're on mobile
  useEffect(() => {
    const checkIfMobile = (): void => {
      setIsMobile(window.innerWidth <= 480);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Add click outside handler to deselect message
  useEffect(() => {
    if (isMobile && isSelected) {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          messageRef.current && 
          !messageRef.current.contains(e.target as Node) &&
          !(e.target instanceof Element && e.target.closest('.MessageMenuMobile'))
        ) {
          setIsSelected(false);
        }
      };
      
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMobile, isSelected]);

  // Call onContentLoad when the component mounts
  useEffect(() => {
    if (isLastMessage && onContentLoad) {
      onContentLoad();
    }
  }, [isLastMessage, onContentLoad]);

  // Add this handler to prevent global scroll when touching messages 
  // but allow scrolling within the message list
  const preventTouchScroll = (e: React.TouchEvent): void => {
    // Don't prevent scrolling - just stop propagation to the parent window
    // This allows scrolling in the message list while preventing global scroll
    e.stopPropagation();
  };

  const handleMessageClick = (event: React.MouseEvent): void => {
    // Don't show menu if editing is active, clicking avatar, playable, or image
    if (
      isEditing ||
      event.target instanceof Element && (
        event.target.closest(`.${styles.messageAvatar}`) ||
        event.target.closest(`.${styles.playable}`) ||
        event.target.closest(`.${styles.imageAttachment}`) ||
        event.target.closest(`.${styles.messageOptions}`) ||
        event.target.closest(`.${styles.regenerationNavigation}`)
      )
    ) {
      return;
    }

    event.preventDefault();

    if (isMobile) {
      // Toggle selected state on mobile
      setIsSelected(!isSelected);
      // If showing menu, hide it when deselecting
      if (showMobileMenu && isSelected) {
        setShowMobileMenu(false);
      }
    } else {
      // Show desktop menu
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;

      // Adjust menu position to ensure it stays within viewport
      const adjustedX = Math.min(x, window.innerWidth - 200); // 200 is approx menu width
      const adjustedY = Math.min(y, window.innerHeight - 200); // 200 is approx max menu height

      setMenuPosition({ x: adjustedX, y: adjustedY });
    }
  };

  const handlePreviousRegeneration = () => {
    if (regenerationHistory && currentRegenerationIndex < regenerationHistory.length && onSelectRegeneration) {
      // Removed console.log for navigating to previous regeneration
      onSelectRegeneration(currentRegenerationIndex + 1);
    }
  };

  const handleNextRegeneration = () => {
    if (currentRegenerationIndex > 0 && onSelectRegeneration) {
      // Removed console.log for navigating to next regeneration
      onSelectRegeneration(currentRegenerationIndex - 1);
    }
  };

  const handleOptionsClick = (): void => {
    if (isMobile) {
      setShowMobileMenu(true);
    } else {
      const element = messageRef.current;
      if (element) {
        const rect = element.getBoundingClientRect();
        setMenuPosition({ x: rect.right - 20, y: rect.top + 20 });
      }
    }
  };

  const handleCopy = async (content: string): Promise<void> => {
    if (content) {
      await navigator.clipboard.writeText(content);
    }
  };

  const handleEdit = (): void => {
    setIsEditing(true);
    setMenuPosition(null);
    setShowMobileMenu(false);
  };

  // Add logging for regenerate
  const handleRegenerate = (): void => {
    // Removed console.log for regenerate triggered
    if (onRegenerate) {
      onRegenerate();
      // Note: Auto-reset now handled by the useEffect above
    }
  };

  return (
    <div
      ref={messageRef}
      className={`${styles.messageWrapper} ${isUserlike(message) ? styles.userMessage : styles.characterMessage}`}
      onTouchMove={preventTouchScroll}
      onClick={handleMessageClick}
      data-message-id={message.id}
      {...otherProps}
    >
      {(displayContent || message.attachment || message.embeds || isLoading) && (
        <>
          <img 
            src={message.icon || personIcon}
            alt={message.display_name}
            className={`${styles.messageAvatar} ${isUserlike(message) ? styles.userAvatar : ''}`}
            onClick={(e) => {
              e.stopPropagation();  // Stop event from bubbling up
              if (message.member_type === 'character') {
                navigate(`/chars/${message.username}`);
              } else if (isUserlike(message) && onAvatarClick) {
                onAvatarClick(e);
              }
            }}
          />
          <div className={`${styles.messageContainer} ${isUserlike(message) ? styles.user : styles[message.member_type]} ${isSelected ? styles.selected : ''}`}>
            <div className={styles.displayNameRow}>
              <div className={styles.displayName}>
                {message.display_name}
                {isRegenerating && <span className={styles.regeneratingLabel}>Regenerating...</span>}
              </div>
            </div>

            <div className={styles.messageContent}>
              {message.embeds ? (
                (() => {
                  console.log('Message embeds:', message.embeds);
                  
                  // Handle image embeds
                  if (message.embeds.type === 'image') {
                    return (
                      <div className={styles.messageContent}>
                        <div className={styles.imageAttachment}>
                          <img 
                            src={message.embeds.image?.url} 
                            alt="Image Embed" 
                            onLoad={onContentLoad}
                          />
                        </div>
                        {displayContent && message.content_type === 'message' && (
                          <div className={`${styles.message} ${isUserlike(message) ? styles.user : styles[message.member_type]}`}>
                            <MarkdownContent content={displayContent} />
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  // Handle Spotify track embeds - support both possible types and ID field names
                  if (message.embeds.type === 'spotify-track' || message.embeds.type === 'spotify') {
                    const trackId = message.embeds.track_id || message.embeds.trackId || 
                                    (message.embeds.track && message.embeds.track.id);
                    
                    console.log('Using Spotify trackId:', trackId);
                    
                    return (
                      <div className={styles.messageContent}>
                        <SpotifyTrackEmbed trackId={trackId} />
                        {displayContent && message.content_type === 'message' && (
                          <div className={`${styles.message} ${isUserlike(message) ? styles.user : styles[message.member_type]}`}>
                            <MarkdownContent content={displayContent} />
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  // Handle playable embeds
                  if (message.embeds.type === 'playable') {
                    return (
                      <div className={styles.playable}>
                        <div className={styles.playableImage}>
                          <img src={message.embeds.image} alt={message.embeds.title} />
                          <div className={styles.playButton}>
                            <img src={playIcon} alt="Play" />
                          </div>
                        </div>
                        <div className={styles.playableTitle}>{message.embeds.title}</div>
                      </div>
                    );
                  }
                  
                  return null;
                })()
              ) : message.attachment ? (
                <div className={styles.messageContent}>
                  <div className={styles.imageAttachment}>
                    <img 
                      src={message.attachment} 
                      alt="Attachment" 
                      onLoad={onContentLoad}
                    />
                  </div>
                  {displayContent && message.content_type === 'message' && (
                    <div className={`${styles.message} ${isUserlike(message) ? styles.user : styles[message.member_type]}`}>
                      <MarkdownContent content={displayContent} />
                    </div>
                  )}
                </div>
              ) : (
                (message.content_type === 'message' && (displayContent || isLoading)) && (
                  <div className={`${styles.message} ${isUserlike(message) ? styles.user : styles.character} ${isLoading ? styles.loading : ''} ${message.isPending ? styles.pending : ''} ${message.sendFailed ? styles.failed : ''}`}>
                    {displayContent && !isRegenerating ? (
                      <MarkdownContent content={displayContent} />
                    ) : isLoading ? (
                      <div className={styles.loadingContainer}>
                        <LoadingDots />
                      </div>
                    ) : null}

                    {message.sendFailed && (
                      <div className={styles.statusIndicator}>Failed to send. Tap to retry.</div>
                    )}
                    
                    {/* Mobile message options - only show when message is selected */}
                    {isMobile && !isEditing && isSelected && (
                      <MessageOptions 
                        onClick={handleOptionsClick} 
                        isMobile={isMobile} 
                      />
                    )}
                  </div>
                )
              )}
            </div>

            {/* Regeneration navigation placed below message content */}
            {regenerationHistory && regenerationHistory.length > 0 && isLastCharacterMessage && (
              <RegenerationNavigation
                total={regenerationHistory.length}
                current={currentRegenerationIndex}
                onPrevious={handlePreviousRegeneration}
                onNext={handleNextRegeneration}
                onRegenerate={handleRegenerate}
                isLoading={isLoading}
              />
            )}

            {/* Desktop menu */}
            {menuPosition && !isMobile && (
              <MessageMenu
                position={menuPosition}
                onClose={() => setMenuPosition(null)}
                onCopy={handleCopy}
                onNewChat={() => onNewChat && onNewChat()}
                onRewind={() => onRewind && onRewind()}
                onEdit={handleEdit}
                onRegenerate={handleRegenerate}
                isLastMessage={isLastMessage}
                isCharacterMessage={message.member_type === 'character' ? true : false}
                message={message}
                isRegenerating={isRegenerating}
              />
            )}

            {/* Mobile menu */}
            {showMobileMenu && isMobile && (
              <MessageMenuMobile
                onClose={() => {
                  setShowMobileMenu(false);
                  setIsSelected(false);
                }}
                onCopy={handleCopy}
                onNewChat={() => onNewChat && onNewChat()}
                onRewind={() => onRewind && onRewind()}
                onEdit={handleEdit}
                onRegenerate={handleRegenerate}
                isLastMessage={isLastMessage}
                isCharacterMessage={message.member_type === 'character' ? true : false}
                message={message}
                isRegenerating={isRegenerating}
              />
            )}

            {/* Desktop edit popup */}
            {!isMobile && (
              <EditMessagePopup
                message={message}
                isVisible={isEditing}
                onClose={() => setIsEditing(false)}
                onSave={async (content: string) => {
                  if (onEdit) {
                    await onEdit(content);
                  }
                }}
              />
            )}

            {/* Mobile edit popup */}
            {isMobile && (
              <EditMessagePopupMobile
                message={message}
                isVisible={isEditing}
                onClose={() => {
                  setIsEditing(false);
                  setIsSelected(false);
                }}
                onSave={async (content: string) => {
                  if (onEdit) {
                    await onEdit(content);
                  }
                  setIsSelected(false);
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Message; 